import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import CrossSectionIcon from '~/assets/tool-icons/cross-section.svg?react';
import DeformIcon from '~/assets/tool-icons/deform.svg?react';
import LineDeformIcon from '~/assets/tool-icons/line_deform.svg?react';
import SmoothBrushIcon from '~/assets/tool-icons/smooth_brush.svg?react';
import {
    AdaptiveBrushIcon,
    BrushIcon,
    EraseIcon,
    LineIcon,
    TransformIcon,
} from '~/assets/workspace-icons';
import { editBlockedReasonId } from '~/components/Structures/editBlockedMessage';
import {
    reasonWhileDisabled,
    refused,
    type ToolEntry,
} from '~/components/toolbar/shared/toolEntry';
import {
    isParkedContouringTool,
    useContouringToolsParked,
} from '~/components/toolbar/shared/useParkedContouringTool';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import {
    brushOptionsState,
    heldToolState,
    isMoveTool,
    lineOptionsState,
    pickedToolState,
    Tools,
} from '~/state/atoms/toolsAtoms';

/** The contouring tools, keyed in toolbar order. */
export interface ContouringToolEntries {
    staticBrush: ToolEntry;
    adaptiveBrush: ToolEntry;
    lineDraw: ToolEntry;
    erase: ToolEntry;
    transform: ToolEntry;
    lineDeform: ToolEntry;
    deform: ToolEntry;
    smoothBrush: ToolEntry;
    crossSection: ToolEntry;
}

/**
 * The contouring tools as {@link ToolEntry} rows, keyed in toolbar order:
 * each with its state, its key and the reason it is refused. Has no
 * effects, so any number of surfaces can call it.
 */
export function useContouringToolEntries(): ContouringToolEntries {
    const intl = useIntl();
    const [tool, pickTool] = useRecoilState(pickedToolState);
    const heldTool = useRecoilValue(heldToolState);
    const {
        isPointRoi,
        isTilted,
        selectionEditBlocker,
        isBulkSelectionActive,
        hasSelectedRoi,
        selectedRoiAnyPlaneEditDisabled,
        selectedRoiContourEditDisabled,
        targetUnresolved,
        isPrimaryImageIncomplete,
        imageIncompleteReason,
    } = useStructureToolGating();
    const [brushOptions, setBrushOptions] = useRecoilState(brushOptionsState);
    const [lineOptions, setLineOptions] = useRecoilState(lineOptionsState);

    const noRoiSelected = !hasSelectedRoi;
    const isMoveTransformDisabled =
        noRoiSelected ||
        isTilted ||
        selectionEditBlocker !== undefined ||
        isBulkSelectionActive ||
        // Transform/move POI writes contour geometry just as the brush does.
        isPrimaryImageIncomplete;

    /**
     * Why a contouring tool is disabled, or undefined when it is not. Tools
     * that edit on any plane are not gated by a tilted grid, so that reason
     * is skipped for them.
     */
    const disabledReasonFor = useCallback(
        (gatedByTiltedGrid: boolean): string | undefined => {
            // Checked first: targetUnresolved also drives isTilted
            // conservatively true (see useStructureToolGating), so it must
            // win over the oblique reason below or the tooltip would name
            // the wrong cause.
            if (targetUnresolved) {
                return intl.formatMessage({
                    id: 'tool.disabled.gridTargetUnresolved',
                });
            }
            // Next, because it is the one condition the user cannot act on
            // at all: no selection change fixes an image that is still
            // arriving.
            if (isPrimaryImageIncomplete) {
                return imageIncompleteReason;
            }
            if (isBulkSelectionActive) {
                return intl.formatMessage({
                    id: 'tool.disabled.bulkSelection',
                });
            }
            if (selectionEditBlocker) {
                return intl.formatMessage({
                    id: editBlockedReasonId(selectionEditBlocker),
                });
            }
            if (gatedByTiltedGrid && isTilted) {
                return intl.formatMessage({ id: 'tool.disabled.oblique' });
            }
            if (isPointRoi) {
                return intl.formatMessage({ id: 'tool.disabled.pointRoi' });
            }
            if (noRoiSelected) {
                return intl.formatMessage({ id: 'tool.disabled.noRoi' });
            }
            return undefined;
        },
        [
            intl,
            targetUnresolved,
            selectionEditBlocker,
            isTilted,
            isPointRoi,
            noRoiSelected,
            isBulkSelectionActive,
            isPrimaryImageIncomplete,
            imageIncompleteReason,
        ]
    );
    const disabledReason = useMemo(
        () => disabledReasonFor(true),
        [disabledReasonFor]
    );
    const anyPlaneDisabledReason = useMemo(
        () => disabledReasonFor(false),
        [disabledReasonFor]
    );

    // A box on screen parks the tools that write to the ROI. They
    // must READ as unavailable for as long as it lasts: a button that looks
    // armed and does nothing is the same lie as a toggle reading on with no
    // tool armed. Which tools those are is asked of the parking hook, never
    // re-listed here — a divergence would leave a parked tool looking live.
    const isParked = useContouringToolsParked();
    const parkedFor = (armedTool: Tools) =>
        isParked && isParkedContouringTool(armedTool);

    // Parking comes LAST: it is transient, and stating it over a standing
    // reason (approved, no ROI) would send the user to close a box that was
    // never what stopped them. `standingReason` is the gate the tool answers
    // to: the tilted-grid one for tools that need an image slice plane, the
    // any-plane one for those that do not.
    const reasonFor = (
        armedTool: Tools,
        standingReason: string | undefined
    ): string | undefined =>
        standingReason ??
        (parkedFor(armedTool)
            ? intl.formatMessage({ id: 'tool.disabled.regionBox' })
            : undefined);

    // Each entry's tool as a test of the armed or the held tool, so the two
    // read the same.
    const isStaticBrush = (t: Tools | null) =>
        t === 'brush' && brushOptions.mode === 'static' && !brushOptions.erase;
    const isAdaptiveBrush = (t: Tools | null) =>
        t === 'brush' && brushOptions.mode === 'smart' && !brushOptions.erase;
    const isLineDrawSelected = tool === 'line' && lineOptions.mode === 'draw';
    // The eraser is one tool with two shapes: the erasing brush and the
    // rectangle that takes whole curves. Both keep the entry lit, so
    // switching shape inside the tray does not read as leaving the tool.
    const isErase = (t: Tools | null) =>
        (t === 'brush' && brushOptions.erase) || t === 'rectangle-select';
    const is = (armedTool: Tools) => (t: Tools | null) => t === armedTool;
    // One entry arms two different tools, and only one of them is parked: a
    // POI move cannot reshape a contour, so the entry stays live for a point
    // ROI even with a box on screen.
    const transformTool: Tools = isPointRoi ? 'poi-move' : 'transform';
    const isTransform = (t: Tools | null) => t !== null && isMoveTool(t);

    // The options are set with the pick, so a held brush arms in the mode
    // picked.
    const armBrush = (mode: 'static' | 'smart', erase: boolean) => {
        pickTool('brush');
        setBrushOptions((options) => ({ ...options, mode, erase }));
    };

    const armLineDraw = () => {
        pickTool('line');
        setLineOptions((options) => ({ ...options, mode: 'draw' }));
    };

    /**
     * Builds an entry whose toolbar click returns it to pan while it is
     * armed or held. `shows` tells whether a tool is the entry's: the armed
     * one makes it selected, the held one held. `arm` leaves an armed tool
     * as it is, so picking it again from a menu never changes its mode.
     * `isArmed` defaults to `selected`, and is narrower where one button
     * covers two modes: a lit Line Draw in erase mode is not armed, so its
     * key returns it to drawing.
     */
    const entry = (
        fields: Omit<
            ToolEntry,
            'arm' | 'toggle' | 'disabledReason' | 'selected' | 'held'
        > & {
            shows: (t: Tools | null) => boolean;
            reason: string | undefined;
            isArmed?: boolean;
            doArm: () => void;
        }
    ): ToolEntry => {
        const { shows, reason, isArmed, doArm, ...rest } = fields;
        const selected = shows(tool);
        const held = shows(heldTool);
        const armed = isArmed ?? selected;
        const disabledReason = reasonWhileDisabled(intl, rest.disabled, reason);
        return {
            ...rest,
            selected,
            held,
            disabledReason,
            arm: () => {
                if (!refused({ ...rest, disabledReason }) && !armed) {
                    doArm();
                }
            },
            toggle: () => (armed || held ? pickTool('pan') : doArm()),
        };
    };

    const nameOf = (id: string) => intl.formatMessage({ id });
    const anyPlaneDisabled = selectedRoiAnyPlaneEditDisabled;
    const contourDisabled = selectedRoiContourEditDisabled;

    return {
        staticBrush: entry({
            id: 'static-brush',
            label: nameOf('staticBrush'),
            icon: <BrushIcon />,
            hotkey: 'b',
            shows: isStaticBrush,
            disabled: anyPlaneDisabled || parkedFor('brush'),
            reason: reasonFor('brush', anyPlaneDisabledReason),
            doArm: () => armBrush('static', false),
        }),
        adaptiveBrush: entry({
            id: 'adaptive-brush',
            label: nameOf('adaptiveBrush'),
            icon: <AdaptiveBrushIcon />,
            hotkey: 'a',
            shows: isAdaptiveBrush,
            disabled: anyPlaneDisabled || parkedFor('brush'),
            reason: reasonFor('brush', anyPlaneDisabledReason),
            doArm: () => armBrush('smart', false),
        }),
        lineDraw: entry({
            id: 'line-draw',
            label: nameOf('toolbar.lineDraw.tooltip'),
            icon: <LineIcon />,
            hotkey: 'l',
            // Lit in draw and erase mode; the tray's Erase toggle tells them
            // apart. A click on it in erase mode returns it to draw.
            shows: is('line'),
            isArmed: isLineDrawSelected,
            disabled: contourDisabled || parkedFor('line'),
            reason: reasonFor('line', disabledReason),
            doArm: armLineDraw,
        }),
        erase: entry({
            id: 'erase',
            label: nameOf('toolbar.erase.tooltip'),
            icon: <EraseIcon />,
            // E is the line tool's draw/erase switch.
            hotkey: 'x',
            shows: isErase,
            disabled: anyPlaneDisabled || parkedFor('brush'),
            reason: reasonFor('brush', anyPlaneDisabledReason),
            // Always opens on the brush shape: the rectangle is reached from
            // the tray, so arming the eraser cannot land the user in a
            // gesture they did not pick. Erase uses the static brush's
            // behaviour with the erase flag on.
            doArm: () => armBrush('static', true),
        }),
        transform: entry({
            id: 'transform',
            label: nameOf(
                isPointRoi
                    ? 'toolbar.movePoi.tooltip'
                    : 'toolbar.transform.tooltip'
            ),
            hint: isPointRoi
                ? undefined
                : 'drag to move, structure edge to scale',
            icon: <TransformIcon />,
            hotkey: 't',
            shows: isTransform,
            disabled: isMoveTransformDisabled || parkedFor(transformTool),
            reason: reasonFor(transformTool, disabledReason),
            doArm: () => pickTool(transformTool),
        }),
        lineDeform: entry({
            id: 'line-deform',
            label: nameOf('toolbar.lineDeform.tooltip'),
            icon: <LineDeformIcon />,
            shows: is('lineDeform'),
            disabled: contourDisabled || parkedFor('lineDeform'),
            reason: reasonFor('lineDeform', disabledReason),
            doArm: () => pickTool('lineDeform'),
        }),
        deform: entry({
            id: 'deform',
            label: nameOf('toolbar.deform.tooltip'),
            icon: <DeformIcon />,
            hotkey: 'd',
            shows: is('deform'),
            disabled: anyPlaneDisabled || parkedFor('deform'),
            reason: reasonFor('deform', anyPlaneDisabledReason),
            doArm: () => pickTool('deform'),
        }),
        smoothBrush: entry({
            id: 'smooth-brush',
            label: nameOf('toolbar.smoothBrush.tooltip'),
            icon: <SmoothBrushIcon />,
            hotkey: 's',
            shows: is('smoothBrush'),
            disabled: anyPlaneDisabled || parkedFor('smoothBrush'),
            reason: reasonFor('smoothBrush', anyPlaneDisabledReason),
            doArm: () => pickTool('smoothBrush'),
        }),
        crossSection: entry({
            id: 'cross-section',
            label: nameOf('toolbar.crossSection.tooltip'),
            hint: intl.formatMessage({ id: 'crossSectionTool.hint' }),
            icon: <CrossSectionIcon />,
            hotkey: 'c',
            shows: is('cross-section'),
            disabled: anyPlaneDisabled || parkedFor('cross-section'),
            reason: reasonFor('cross-section', anyPlaneDisabledReason),
            doArm: () => pickTool('cross-section'),
        }),
    };
}
